import { QuestionCircleOutlined } from '@ant-design/icons'
import { useChannels } from '@entities/channel'
import type { FormProps } from 'antd'
import { Button, Card, Checkbox, Col, Form, Input, Row, Select, Space, theme, Tooltip, Typography } from 'antd'
import axios from 'axios'
import { useEffect } from 'react'
import InputMask from 'react-input-mask'

type OrganizationType = 'ul' | 'ip' | 'fl'

type OrdFormFields = {
    organization?: string
    type: OrganizationType
    name: string
    mobilePhone?: string
    kpp?: string
    inn: string
    fio?: string
    ogrn: string
    address: string
    isORS: boolean
    isRR: boolean
    rsUrl?: string
}

const { Text } = Typography

export const OrdFormMobile = () => {
    const {
        actions: { editChannel },
        channelDetails
    } = useChannels()

    const {
        token: { colorWhite }
    } = theme.useToken()

    const [form] = Form.useForm<OrdFormFields>()

    const onFinish: FormProps<OrdFormFields>['onFinish'] = async (values) => {
        if (!channelDetails?.id) return

        editChannel({
            id: channelDetails.id,
            data: {
                settings: {
                    ord: {
                        ...values
                    }
                }
            }
        })
    }

    const isORSChecked = Form.useWatch('IsORS', form)

    useEffect(() => {
        if (!channelDetails?.settings?.ord) return

        const { organization, type, name, mobilePhone, kpp, inn, fio, ogrn, address, isORS, isRR, rsUrl } =
            channelDetails.settings.ord

        form.setFieldsValue({
            organization,
            type: type as OrganizationType,
            inn,
            kpp,
            name,
            fio,
            mobilePhone,
            ogrn,
            address,
            isORS,
            isRR,
            rsUrl
        })
    }, [form, channelDetails])

    const orgType = Form.useWatch('type', form)

    return (
        <Form<OrdFormFields>
            form={form}
            name="add-partner"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            onFinish={onFinish}
            initialValues={{
                type: 'ul'
            }}
            style={{ paddingBottom: '91px' }}
        >
            <Card size="small" style={{ backgroundColor: colorWhite }}>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={
                                <Space>
                                    <Text style={{ fontSize: '17px', color: '#8F8E94' }}>Организация</Text>
                                    <Tooltip title="Добавленной организации будет присвоен ID, который будет отправлен ЕРИР.">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            }
                            name="organization"
                            required={false}
                        >
                            <Input readOnly={true} size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>Тип организации</Text>}
                            name="type"
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Select
                                placeholder="Выберите тип"
                                options={[
                                    { label: 'Юридическое лицо', value: 'ul' },
                                    { label: 'Индивидуальный предприниматель', value: 'ip' },
                                    { label: 'Физическое лицо', value: 'fl' }
                                ]}
                                size="large"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>ИНН</Text>}
                            name="inn"
                            validateTrigger="onBlur"
                            validateDebounce={500}
                            rules={[
                                { required: true, message: 'Обязательное поле' },
                                {
                                    validator: async (_, value) => {
                                        if (orgType === 'fl') {
                                            return Promise.resolve()
                                        }

                                        if (!value) return Promise.resolve()

                                        // защита от гонок: валидируем только актуальное значение
                                        const typed = value

                                        try {
                                            const { data } = await axios.get(`https://egrul.itsoft.ru/${value}.json`)

                                            // если пользователь уже поменял ИНН во время запроса — выходим
                                            if (typed !== value) return Promise.resolve()

                                            // сервер ответил с ошибкой в payload
                                            if (data?.error?.message) {
                                                return Promise.reject(new Error(data.error.message))
                                            }

                                            const ipData = data['СвИП']
                                            const ulData = data['СвЮЛ']

                                            if (ipData) {
                                                const fioAttrs = ipData?.['СвФЛ']?.['ФИОРус']?.['@attributes'] ?? {}
                                                const fio = [fioAttrs['Фамилия'], fioAttrs['Имя'], fioAttrs['Отчество']]
                                                    .filter(Boolean)
                                                    .join(' ')
                                                const ogrn = ipData?.['@attributes']?.['ОГРНИП'] ?? ''

                                                form.setFieldsValue({ fio, ogrn })
                                            } else if (ulData) {
                                                const ogrn = ulData?.['@attributes']?.['ОГРН'] ?? ''
                                                const kpp = ulData?.['@attributes']?.['КПП'] ?? ''
                                                const name = ulData?.['СвНаимЮЛ']?.['@attributes']?.['НаимЮЛПолн'] ?? ''

                                                form.setFieldsValue({ name, ogrn, kpp })
                                            }

                                            return Promise.resolve()
                                        } catch (err) {
                                            // если это ответ сервера с ошибкой — валим валидацию с его message
                                            if (axios.isAxiosError(err) && err.response?.data?.error?.message) {
                                                return Promise.reject(new Error('Запись с таким ИНН не найдена'))
                                            }
                                            // сетевой/прочий фейл без ответа сервера — пропускаем валидацию
                                            return Promise.resolve()
                                        }
                                    }
                                }
                            ]}
                        >
                            <Input placeholder="Введите ИНН" size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                {orgType === 'ul' && (
                    <>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>КПП</Text>}
                                    name="kpp"
                                >
                                    <Input placeholder="Введите КПП" size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label={
                                        <Text style={{ fontSize: '17px', color: '#8F8E94' }}>
                                            ОПФ и полное наименование компании
                                        </Text>
                                    }
                                    name="name"
                                >
                                    <Input placeholder="Введите ОФП" size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {(orgType === 'ip' || orgType === 'fl') && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>ФИО</Text>}
                                name="fio"
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Input placeholder="Введите ФИО" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {(orgType === 'ip' || orgType === 'ul') && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>ОГРН</Text>}
                                name="ogrn"
                            >
                                <Input placeholder="Введите огрн" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={<Text style={{ fontSize: '17px', color: '#8F8E94' }}>Адрес</Text>}
                            name="address"
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Input placeholder="Введите адрес" size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={
                                <Text style={{ fontSize: '17px', color: '#8F8E94' }}>
                                    Абонентский номер мобильного телефона
                                </Text>
                            }
                            name="mobilePhone"
                        >
                            <InputMask mask="+7(999)999-99-99" maskChar={null}>
                                {(inputProps) => (
                                    <Input {...inputProps} placeholder="Введите номер телефона" size="large" />
                                )}
                            </InputMask>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="IsORS" valuePropName="checked">
                            <Checkbox>
                                <Space>
                                    <Text style={{ fontSize: '17px', color: '#8F8E94' }}>Является ОРС </Text>
                                    <Tooltip title="Является ли организация Оператором Рекламной Системы? Рекламная система показывает рекламу как на собственных площадках, так и на внешних площадках, пример - РСЯ">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="isRR" valuePropName="checked">
                            <Checkbox>
                                <Space>
                                    <Text style={{ fontSize: '17px', color: '#8F8E94' }}>Является РР</Text>
                                    <Tooltip title="Является ли организация Рекламораспространителем? Речь про собственные рекламные площадки, пример - площадка которая показывает рекламу на своем сайте через РСЯ">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                {isORSChecked && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="URL рекламной системы"
                                name="orsUrl"
                                rules={[{ required: true, message: 'Введите URL' }]}
                            >
                                <Input placeholder="https://..." size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Card>

            <Row gutter={16}>
                <Col span={24}>
                    <div
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            padding: '8px 16px 42px 16px',
                            zIndex: 10000,
                            width: '100%',
                            backgroundColor: colorWhite,
                            borderTop: '0.5px solid #D3D3D5'
                        }}
                    >
                        <Button type="primary" htmlType="submit" size="large" style={{ width: '100%' }}>
                            Сохранить
                        </Button>
                    </div>
                </Col>
            </Row>
        </Form>
    )
}
